import React, {useCallback, useContext, useEffect} from 'react';
import cx from 'classnames';
import {EmptyState} from '../EmptyState/EmptyState';
import {Draggable, noop, PriorityIcon, Scrollbar} from '@priority-software/priority-style-react';
import DraggableList from '../../../../../../components/Common/DraggableList';
import {useScrollWrapDimensions} from '../../../../../../hooks/customScroll/useScrollWrapDimensions';
import {OverviewCard} from './OverviewCard';
import {AutomationContext} from '../../../../context';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {automationOverviewListID} from '../../../../testIDs';
import {RuleType} from '../../../../enums';
import {AutomationsOverviewContext} from '../../RulesListDialog';
import {AlertListItem} from '../../../../reducer/types';
import {isRTL} from '../../../../../../Localization';
import {showFormClientSideMessage} from '../../../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';
import {texts} from '../../../../texts';
import styles from '../../styles.module.scss';
import {sendMonitor} from '../../../../../../utils/monitoring';

import {
    BusinessRuleListDelete,
    BusinessRuleListDragging,
    BusinessRuleListDuplicate
} from '../../consts';
import {BusinessRuleAction} from '../../../../constants';

export const OverviewCardsList = ({type, session}: {type: RuleType; session: string}) => {
    const {
        removeRule,
        copyRule,
        editRule,
        selectedRuleIndex,
        searchValue,
        filteredRules: data,
        appliedFilters,
        handleRuleActive,
        handleRuleOrder,
        setSelectedRuleIndex
    } = useContext(AutomationContext);

    const resultsCount = data.length;
    const {lockKeyboardInteraction} = useContext(AutomationsOverviewContext);

    const {setWrapRef, maxHeight: scrollMaxHeight} = useScrollWrapDimensions();
    const isDraggable = appliedFilters.length === 0 && searchValue.length === 0;

    const handleCopy = useCallback(
        async (alertNumber: number) => {
            if (type === RuleType.business) {
                sendMonitor(BusinessRuleListDuplicate);
            }
            await copyRule(alertNumber);
            setSelectedRuleIndex(data.length);
        },
        [copyRule, data.length, setSelectedRuleIndex]
    );

    const handleSelectItem = useCallback(
        (ruleNumber: number) => {
            const index = data.findIndex(item => item.alertNumber === ruleNumber);
            if (typeof index === 'number') {
                setSelectedRuleIndex(index);
            }
        },
        [data, setSelectedRuleIndex]
    );

    const onEditRule = useCallback(
        (ruleNumber: number, fetchHtml?: boolean) => {
            editRule(ruleNumber, fetchHtml);
            handleSelectItem(ruleNumber);
        },
        [handleSelectItem, editRule]
    );

    useEffect(() => {
        if (resultsCount && !lockKeyboardInteraction) {
            const handleSelectedRules = (e: KeyboardEvent) => {
                if (e.keyCode === 38) {
                    e.preventDefault();
                    setSelectedRuleIndex(Math.max(selectedRuleIndex - 1, 0));
                } else if (e.keyCode === 40) {
                    e.preventDefault();
                    setSelectedRuleIndex(Math.min(selectedRuleIndex + 1, resultsCount - 1));
                } else if (e.keyCode === 13) {
                    e.preventDefault();
                    // handle `Enter` key to open focused rule.
                    const selectedRule = data[selectedRuleIndex];
                    const alertNumber = selectedRule?.alertNumber;

                    if (alertNumber) {
                        e.preventDefault();
                        onEditRule(
                            alertNumber,
                            selectedRule.ruleAction === BusinessRuleAction.EmailMsg
                        );
                    }
                }
            };
            document.addEventListener('keydown', handleSelectedRules);
            return () => document.removeEventListener('keydown', handleSelectedRules);
        }
        return noop;
    }, [
        resultsCount,
        lockKeyboardInteraction,
        onEditRule,
        data,
        selectedRuleIndex,
        setSelectedRuleIndex
    ]);

    const handleDNDOrder = useCallback(
        async (items: Array<AlertListItem>, destinationIndex: number) => {
            if (isDraggable) {
                if (type === RuleType.business) {
                    sendMonitor(BusinessRuleListDragging);
                }
                handleRuleOrder(items);
                setSelectedRuleIndex(destinationIndex);
            }
        },
        [handleRuleOrder, setSelectedRuleIndex, type]
    );

    const onRemove = useCallback(
        (ruleNumber: number) => {
            if (type === RuleType.business) {
                sendMonitor(BusinessRuleListDelete);
            }
            showFormClientSideMessage(session, priority.netitems.shared.Message.Type.Warning, {
                title: texts.delete_rule_title,
                content: texts.delete_rule_text,
                messageIcon: PriorityIcon.delete,
                onConfirm: async () => removeRule(ruleNumber)
            });
        },
        [removeRule, session, type]
    );

    const onDragStart = useCallback(() => {
        setSelectedRuleIndex(-1);
    }, [setSelectedRuleIndex]);

    return (
        <div
            className={styles.rulesList}
            ref={setWrapRef}
            {...createDataTestId(automationOverviewListID)}
        >
            {data.length === 0 ? (
                <EmptyState
                    searchValue={searchValue}
                    hasFilters={appliedFilters.length > 0}
                    type={type}
                />
            ) : (
                <Scrollbar autoHeight autoHeightMax={scrollMaxHeight} rtl={isRTL()}>
                    <DraggableList
                        data={data}
                        handleDNDFinish={handleDNDOrder}
                        onDragStart={onDragStart}
                        componentClasses={cx(styles.rulesListInner, {
                            [styles.disabledDrag]: !isDraggable
                        })}
                        renderItem={({item, key: draggableKey, ...draggableProps}) => {
                            const key = item.alertNumber.toString();
                            return (
                                <Draggable
                                    key={key}
                                    componentClasses={styles.dragHandle}
                                    {...draggableProps}
                                    iconPosition="top"
                                >
                                    <OverviewCard
                                        key={`${key}-expanded:${!!searchValue}`}
                                        ruleType={type}
                                        onClick={handleSelectItem}
                                        searchText={searchValue}
                                        onEdit={onEditRule}
                                        onRemove={onRemove}
                                        onCopy={handleCopy}
                                        handleRuleActive={handleRuleActive}
                                        isFocused={selectedRuleIndex === draggableProps.index}
                                        item={item}
                                    />
                                </Draggable>
                            );
                        }}
                    />
                </Scrollbar>
            )}
        </div>
    );
};
